import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, CircularProgress, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Stack, TextField } from '@mui/material';
import { ArrowDownIcon } from '../../../../../../icons/ArrowDownIcon';
import { theme } from '../../../../../common/styles/theme';
import { Check } from '../../../../../common/components/Check/Check';
import { Text } from '../../../../../common/components/Text/Text';
import LangFlag from '../../../shared/LangFlag/LangFlag';
import { TLangSelectOption } from '../../../LangSelect/components/types';
import { CONFERENCE_SAVE_LEVELS, TConferenceSaveLevel, isStricterSaveLevel } from '../../../../../common/rest/conference/conferenceSaveLevel';
import { CallLangs } from '../../langs';
import { IConferenceSettingsProps, TConferenceSettingsSection } from './types';
import { Wording } from '../wording';
import { ConfirmPopup } from '../../../ConfirmPopup/ConfirmPopup';

export function ConferenceSettings({
	title,
	description,
	langCode,
	multilingual,
	provider,
	saveLevel,
	langs,
	options,
	providers,
	isProcessing,
	isConfOwner,
	isAdmin,
	keptExisting,
	log,
	logStatus,
	logTime,
	section = 'info',
	onChange,
	onInfoChange,
	onGenerateInfo,
	onRestoreInfo,
	onProcess,
}: IConferenceSettingsProps) {

	// Per-user title/description (not gated by isConfOwner), each saved on its own blur, so editing one
	// never pins the other as a personal copy.
	const titleField = useInfoField(title, (value) => onInfoChange({ title: value }));
	const descriptionField = useInfoField(description, (value) => onInfoChange({ description: value }));

	// Regenerate saves at once and may be pressed several times; Cancel puts back what was there before
	// the first one of this popup session (the server keeps that state)
	const [isGenerating, setGenerating] = React.useState(false);
	const [isRestoring, setRestoring] = React.useState(false);
	const [canRestore, setCanRestore] = React.useState(false);
	const handleGenerateInfo = async () => {
		setGenerating(true);
		try {
			if (await onGenerateInfo(!canRestore)) setCanRestore(true);
		} finally {
			setGenerating(false);
		}
	};
	const handleRestoreInfo = async () => {
		setRestoring(true);
		try {
			// 'gone' (409) hides Cancel too: pressing it again would fail the same way
			if (await onRestoreInfo() !== 'failed') setCanRestore(false);
		} finally {
			setRestoring(false);
		}
	};

	// Local working values so the controls stay responsive; every change is also persisted via onChange.
	const [code, setCode] = React.useState(langCode);
	const [multi, setMulti] = React.useState(multilingual);
	const [prov, setProv] = React.useState(provider);
	const [level, setLevel] = React.useState<TConferenceSaveLevel>(saveLevel);

	React.useEffect(() => setCode(langCode), [langCode]);
	React.useEffect(() => setMulti(multilingual), [multilingual]);
	React.useEffect(() => setProv(provider), [provider]);
	React.useEffect(() => setLevel(saveLevel), [saveLevel]);

	// Which stages the next run redoes. Per-run (not persisted like the settings above) and both on by
	// default, so pressing Process without touching them rebuilds everything, as it always did.
	const [doTranscribe, setDoTranscribe] = React.useState(true);
	const [doVideo, setDoVideo] = React.useState(true);
	// Mirror the backend's own clamp (video only survives at level 'video') so noSteps and what's
	// submitted agree with what's shown.
	const effectiveDoVideo = doVideo && level === 'video';
	// Nothing selected means nothing to do — the backend rejects it, so don't let it be sent.
	const noSteps = !doTranscribe && !effectiveDoVideo;
	// Transcript-level audio is deleted right after processing, and Nothing-level keeps no audio at
	// all (both enforced backend-side, /reprocess itself now rejects saveLevel=nothing) — reprocessing
	// has nothing to rebuild from at either level.
	const canReprocess = level !== 'transcription' && level !== 'nothing';

	const langMulti = CallLangs.isLangMulti(code);

	const langIdByCode = (c: string) => langs.find(l => l.code === c)?.id;

	// Divider goes after the last lang that belongs to a user group (options are group-first sorted).
	const [lastGroupLangCode, setLastGroupLangCode] = React.useState<string | null>(null);
	React.useEffect(() => {
		let lastGroupLang = '';
		for (let i = 0; i < options.length; i++) {
			if (options[i].itemsCount === null) break;
			lastGroupLang = options[i].code;
		}
		setLastGroupLangCode(lastGroupLang);
	}, [options]);

	const handleLangChange = (e: React.SyntheticEvent, newValue: TLangSelectOption | null) => {
		if (!isConfOwner || isProcessing) return;

		const newCode = newValue?.code || '';
		if (!newCode) return;
		const langId = langIdByCode(newCode);
		if (langId == null) return;
		setCode(newCode);
		// A non-multilingual language forces the toggle off — keep local + backend in sync.
		if (multi && !CallLangs.isLangMulti(newCode)) {
			setMulti(false);
			onChange({ langId, multilingual: false });
		} else {
			onChange({ langId });
		}
	};

	const handleMultiChange = () => {
		if (!isConfOwner || isProcessing) return;

		const next = !multi;
		setMulti(next);
		onChange({ multilingual: next });
	};

	const handleProviderChange = (value: string) => {
		if (!isConfOwner || isProcessing) return;

		setProv(value);
		onChange({ transcriptionProvider: value });
	};

	// Anchor + the level held pending confirmation. Every reachable change here is a downgrade (looser
	// options are disabled above) and each deletes something for good, so all of them are confirmed.
	const saveSelectRef = React.useRef<HTMLDivElement | null>(null);
	const [pendingLevel, setPendingLevel] = React.useState<TConferenceSaveLevel | null>(null);

	const applyLevel = (next: TConferenceSaveLevel) => {
		setLevel(next);
		onChange({ saveLevel: next });
	};

	// Unlike language/provider, any call participant may change this — not gated by isConfOwner.
	const handleLevelChange = (e: any) => {
		if (isProcessing) return;

		const next = e.target.value as TConferenceSaveLevel;
		if (next === level) return;
		if (Wording.SAVE_LEVEL_CONFIRM[next]) {
			setPendingLevel(next); // held until confirmed — the select stays on the current value until then
			return;
		}
		applyLevel(next);
	};

	const handleConfirmLevel = () => {
		if (pendingLevel) applyLevel(pendingLevel);
		setPendingLevel(null);
	};

	// Opens with the requested section (a live run forces Processing). After a successful run, swaps
	// to Info only if the run actually changed the title/description.
	const startOnProcessing = logStatus === 'running' || section === 'processing';
	// only one section expanded at a time; null = both collapsed
	const [openSection, setOpenSection] = React.useState<TConferenceSettingsSection | null>(startOnProcessing ? 'processing' : 'info');
	const infoOpen = openSection === 'info';
	const processingOpen = openSection === 'processing';
	const toggleSection = (s: TConferenceSettingsSection) => setOpenSection(openSection === s ? null : s);
	const runStartInfoRef = React.useRef(logStatus === 'running' ? { title, description } : null);
	const prevLogStatusRef = React.useRef(logStatus);
	React.useEffect(() => {
		const prev = prevLogStatusRef.current;
		prevLogStatusRef.current = logStatus;
		if (logStatus === 'running' && prev !== 'running') {
			runStartInfoRef.current = { title, description };
			setOpenSection('processing');
		} else if (logStatus !== 'running' && logStatus !== 'done') {
			runStartInfoRef.current = null;
		}
	}, [logStatus]);
	// also on title/description: the refreshed recording may land after the run reports done
	React.useEffect(() => {
		const start = runStartInfoRef.current;
		if (logStatus !== 'done' || !start) return;
		if (start.title !== title || start.description !== description) {
			runStartInfoRef.current = null;
			setOpenSection('info');
		}
	}, [logStatus, title, description]);

	const statusSummary = logStatus === 'running'
		// inline, not flex: a flex row takes its baseline from the spinner and shifts the time up
		? <><CircularProgress size={theme.spacing(1.5)} sx={{ verticalAlign: '-2px', mr: .75 }} />{logTime}</>
		: logStatus === 'done'
			? <Box component={'span'} sx={{ color: theme.palette.success.main }}>Done ({logTime})</Box>
			: logStatus === 'failed'
				? <Box component={'span'} sx={{ color: theme.palette.error.main }}>Failed</Box>
				: logStatus === 'cancelled'
					? 'Cancelled'
					: null;

	// each section is a bordered card with a tinted header
	const accordionSx = {
		background: 'none',
		border: `2px solid ${theme.palette.grey[200]}`,
		overflow: 'hidden',
		// MUI's own :first-of-type/:last-of-type radius outranks a plain borderRadius
		'&, &:first-of-type, &:last-of-type': { borderRadius: theme.spacing(1) },
		'&:before': { display: 'none' },
		'.MuiAccordionSummary-root': {
			px: 2,
			minHeight: theme.spacing(6),
			background: theme.palette.grey[200],
			'&:hover': { background: theme.palette.grey[300] },
		},
		'.MuiAccordionSummary-root.Mui-expanded': { borderBottom: `2px solid ${theme.palette.grey[200]}` },
		'.MuiAccordionSummary-content': { m: 0, minWidth: 0, alignItems: 'baseline', gap: 1 },
		'.MuiAccordionDetails-root': { p: 2 },
	};
	const summaryHintSx = {
		minWidth: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		lineHeight: 1.2,
		fontSize: theme.typography.subtitle1.fontSize,
		color: theme.palette.grey[500],
		fontVariantNumeric: 'tabular-nums',
	};
	// disables the controls during a run, but not the log next to them
	const lockSx = {
		'&.processing': {
			pointerEvents: 'none',
			opacity: 0.3,
		},
	};

	return (
		<Stack
			gap={1.5}
			sx={{
				minWidth: theme.spacing(40),
			}}
		>
			{/* recording-level settings, shared by both sections */}
			<Stack
				className={isProcessing ? 'processing' : ''}
				gap={1}
				sx={{
					pb: .5,
					...lockSx,
				}}
			>
				<Stack
					direction={'row'}
					gap={2}
				>
					{/* language is owner-only */}
					<FormControl
						sx={{
							flex: 1,
							minWidth: 0,
							...(!isConfOwner ? { pointerEvents: 'none', opacity: 0.3 } : {}),
						}}
						disabled={!options || !options.length}
					>
						<InputLabel shrink htmlFor={'conference-lang-select'}>
							Language
						</InputLabel>
						<Autocomplete
							autoHighlight
							disabled={!options || !options.length}
							id={'conference-lang-select'}
							options={options}
							getOptionLabel={(option) => option.name}
							value={options.find((option) => option.code === code) || null}
							onChange={handleLangChange}
							PaperComponent={({ children, ...other }) => (
								<Paper {...other} style={{ boxShadow: theme.shadows[8] }}>
									{children}
								</Paper>
							)}
							renderInput={(params) => {
								return <TextField
									{...params}
									variant={'filled'}
									InputProps={{
										...params.InputProps,
										startAdornment: (
											<React.Fragment>
												{params.InputProps.startAdornment}
												<LangFlag code={code} showCode={false} />
											</React.Fragment>
										),
									}}
									InputLabelProps={{
										shrink: true,
									}}
								/>;
							}}
							renderOption={(props, option) => {
								const { key, ...optionProps } = props as any;
								return (
									<React.Fragment key={key}>
										<li {...optionProps}>
											<Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
												<LangFlag code={option.code} showCode={false} />
												<Text variant={'subtitle2'}>
													{option.highlight ? <b>{option.name}</b> : option.name}
												</Text>
											</Stack>
										</li>
										{option.code === lastGroupLangCode && <Divider />}
									</React.Fragment>
								);
							}}
						/>
					</FormControl>

					{/* Not gated by isConfOwner — any call participant may change what gets kept. */}
					<FormControl
						sx={{ flex: 1, minWidth: 0 }}
						disabled={isProcessing}
						ref={saveSelectRef}
					>
						<InputLabel shrink htmlFor={'conference-save-level-select'}>
							Save
						</InputLabel>
						<Select
							fullWidth
							variant={'filled'}
							id={'conference-save-level-select'}
							value={level}
							disabled={isProcessing}
							onChange={handleLevelChange}
							renderValue={(l) => Wording.SAVE_LEVEL_LABELS[l as TConferenceSaveLevel]}
							MenuProps={{ PaperProps: { sx: { minWidth: theme.spacing(40) } } }} // long hints outgrow the half-width select
							sx={{
								'.MuiSelect-select': {
									fontWeight: 400 + ' !important',
								},
							}}
						>
							{CONFERENCE_SAVE_LEVELS.map(l => (
								<MenuItem
									value={l}
									key={`save-level-${l}`}
									disabled={isStricterSaveLevel(level, l)}
									sx={{
										height: 'auto',
									}}
								>
									<Stack>
										<Text variant={'subtitle2'}>{Wording.SAVE_LEVEL_LABELS[l]}</Text>
										<Text variant={'subtitle1'}
											sx={{
												color: theme.palette.grey[500],
												lineHeight: 1.2,
											}}
										>
											{Wording.SAVE_LEVEL_HINTS[l]}
										</Text>
									</Stack>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>

				{!isConfOwner &&
					<Box sx={{ color: theme.palette.grey[500], fontSize: theme.typography.subtitle1.fontSize }}>
						{Wording.NOT_OWNER_TEXT}
					</Box>
				}
			</Stack>

			<Accordion
				disableGutters
				elevation={0}
				expanded={infoOpen}
				onChange={() => toggleSection('info')}
				slotProps={{ transition: { onEntered: revealExpanded } }}
				sx={accordionSx}
			>
				<AccordionSummary expandIcon={<ArrowDownIcon />}>
					<Text variant={'subtitle2'} fontWeight={600} sx={{ flexShrink: 0, lineHeight: 1.2 }}>Info</Text>
					{!infoOpen && !!title &&
						<Box sx={summaryHintSx}>{title}</Box>
					}
				</AccordionSummary>
				<AccordionDetails>
					<Stack
						className={isProcessing ? 'processing' : ''}
						gap={2}
						sx={lockSx}
					>
						<TextField
							fullWidth
							variant={'filled'}
							label={'Title'}
							{...titleField}
							inputProps={{ maxLength: 255 }}
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							fullWidth
							multiline
							minRows={2}
							maxRows={6}
							variant={'filled'}
							label={'Description'}
							{...descriptionField}
							inputProps={{ maxLength: 4000 }}
							InputLabelProps={{ shrink: true }}
						/>
						<Stack
							flexDirection={'row'}
							justifyContent={'flex-end'}
							gap={1}
							sx={{ mt: -1 }}
						>
							{canRestore &&
								<Button
									variant={'text'}
									disabled={isGenerating || isRestoring}
									onClick={handleRestoreInfo}
									sx={{
										color: theme.palette.grey[500],
									}}
								>
									{isRestoring ? 'Restoring…' : 'Cancel'}
								</Button>
							}
							<Button
								variant={'text'}
								disabled={isGenerating || isRestoring}
								onClick={handleGenerateInfo}
							>
								{/* by the saved description, not the field, so it doesn't flicker while typing */}
								{isGenerating
									? 'Generating…'
									: description ? 'Regenerate' : 'Generate title and description'}
							</Button>
						</Stack>
					</Stack>
				</AccordionDetails>
			</Accordion>

			<Accordion
				disableGutters
				elevation={0}
				expanded={processingOpen}
				onChange={() => toggleSection('processing')}
				slotProps={{ transition: { onEntered: revealExpanded } }}
				sx={accordionSx}
			>
				<AccordionSummary expandIcon={<ArrowDownIcon />}>
					<Text variant={'subtitle2'} fontWeight={600} sx={{ flexShrink: 0, lineHeight: 1.2 }}>Processing</Text>
					{!!statusSummary &&
						<Box sx={summaryHintSx}>{statusSummary}</Box>
					}
				</AccordionSummary>
				<AccordionDetails>
					<Stack gap={2}>
						<Stack
							className={isProcessing ? 'processing' : ''}
							gap={2}
							sx={lockSx}
						>
							{isAdmin &&
								<FormControl sx={{ width: 1 }} >
									<InputLabel shrink htmlFor={'provider-select'}>
										Transcription provider
									</InputLabel>
									<Select
										id={'provider-select'}
										variant={'filled'}
										value={prov}
										onChange={(e) => handleProviderChange(e.target.value as string)}
									>
										{providers.map(p => (
											<MenuItem key={p} value={p}>{p}</MenuItem>
										))}
									</Select>
								</FormControl>
							}

							{canReprocess
								? <>
									<Stack>
										<FormControlLabel
											control={
												<Check
													checked={doTranscribe}
													onChange={() => setDoTranscribe(!doTranscribe)}
												/>
											}
											label={'Reprocess transcribe'}
											sx={{
												m: theme.spacing(0, 0, 0, -.75),
											}}
										/>

										<FormControlLabel
											disabled={level !== 'video'}
											control={
												<Check
													disabled={level !== 'video'}
													checked={effectiveDoVideo}
													onChange={() => setDoVideo(!doVideo)}
												/>
											}
											label={level === 'video' ? 'Reprocess video' : 'Reprocess video (Save level is not Video)'}
											sx={{
												m: theme.spacing(0, 0, 0, -.75),
											}}
										/>
									</Stack>

									<Stack
										flexDirection={'row'}
										justifyContent={'flex-end'}
									>
										<Button
											variant={'contained'}
											disabled={!code || !onProcess || noSteps}
											onClick={() => onProcess && !isProcessing && !noSteps
												&& onProcess(keptExisting, { transcribe: doTranscribe, video: effectiveDoVideo })}
										>
											{keptExisting ? 'Force reprocess' : 'Process'}
										</Button>
									</Stack>
								</>
								: <Box sx={{ color: theme.palette.grey[500], fontSize: theme.typography.subtitle1.fontSize }}>
									{Wording.REPROCESS_UNAVAILABLE_TEXT}
								</Box>
							}
						</Stack>

						{log}
					</Stack>
				</AccordionDetails>
			</Accordion>

			<ConfirmPopup
				anchor={pendingLevel ? saveSelectRef.current : null}
				text={(pendingLevel && Wording.SAVE_LEVEL_CONFIRM[pendingLevel]?.text) || ''}
				description={(pendingLevel && Wording.SAVE_LEVEL_CONFIRM[pendingLevel]?.description) || ''}
				confirmTitle={'Ok'}
				closeTitle={'Cancel'}
				onConfirm={handleConfirmLevel}
				onClose={() => setPendingLevel(null)}
			/>
		</Stack>
	);
};

// After an accordion opens, scrolls its container to show as much of it as fits, header kept in view.
function revealExpanded(collapseEl: HTMLElement) {
	const accordion = collapseEl.closest('.MuiAccordion-root') as HTMLElement | null;
	let scrollParent = accordion?.parentElement || null;
	while (scrollParent) {
		const { overflowY } = getComputedStyle(scrollParent);
		if (overflowY === 'auto' || overflowY === 'scroll') break;
		scrollParent = scrollParent.parentElement;
	}
	if (!accordion || !scrollParent) return;
	const box = accordion.getBoundingClientRect();
	const view = scrollParent.getBoundingClientRect();
	const delta = Math.min(box.bottom - view.bottom, box.top - view.top);
	if (delta > 0) scrollParent.scrollBy({ top: delta, behavior: 'smooth' });
}

// One editable field mirroring a store value: synced from the store only while not focused (a save
// response or an AI title can't wipe what's being typed), saved on blur only if actually edited.
function useInfoField(storeValue: string, onSave: (value: string) => void) {
	const [value, setValue] = React.useState(storeValue);
	const focusedRef = React.useRef(false);
	const dirtyRef = React.useRef(false);
	// last value known to be on the server (trimmed, as stored) — the store lags while a save is in flight
	const savedRef = React.useRef(storeValue);
	React.useEffect(() => {
		savedRef.current = storeValue;
		if (!focusedRef.current) setValue(storeValue);
	}, [storeValue]);

	return {
		value,
		onFocus: () => { focusedRef.current = true; },
		onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			dirtyRef.current = true;
			setValue(e.target.value);
		},
		onBlur: () => {
			focusedRef.current = false;
			if (!dirtyRef.current) {
				setValue(savedRef.current); // untouched: pick up whatever arrived while focused
				return;
			}
			dirtyRef.current = false;
			const next = value.trim();
			if (!next) {
				// cleared = reset to the shared value; with no personal copy the store won't change,
				// so restore the field here (a reset copy arrives through the store as usual)
				setValue(savedRef.current);
				if (savedRef.current) onSave(''); // already empty: nothing to reset
				return;
			}
			setValue(next);
			if (next === savedRef.current) return;
			savedRef.current = next;
			onSave(next);
		},
	};
}
